import type {AjaxService} from 'core/data-services/ajax.service';
import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import {exists} from 'shared/utils/custom-utils';
import {transferify} from 'shared/utils/data-service-utils';
import {noop} from 'rxjs';

interface ChunkUploadOptions<RETURN_TYPE> {
    uploadEndpoint: string;
    file: File;
    additionalData: Record<string, any>;
    onProgressUpdate?: (progress: number) => void;
    onUploadFinished?: (result: RETURN_TYPE) => void;
}

interface StartChunkFileUploadResponse {
    chunkedFileHandle: string;
}

export class ChunkFileUploadService {
    public static serviceName = 'chunkFileUploadService';

    /*@ngInject*/
    constructor(private ajaxService: AjaxService, private toastMessageService: ToastMessageService) {
    }

    public createUploader<RETURN_TYPE>(options: ChunkUploadOptions<RETURN_TYPE>): ChunkFileUploader<RETURN_TYPE> {
        return new ChunkFileUploader<RETURN_TYPE>(
            this.ajaxService,
            this.toastMessageService,
            options.uploadEndpoint,
            options.file,
            options.additionalData,
            options.onProgressUpdate ?? noop,
            options.onUploadFinished ?? noop
        );
    }
}

export class ChunkFileUploader<RETURN_TYPE> {

    private static CHUNK_SIZE = 1024 * 512; // 512 kb
    private static UPLOAD_FILE_CHUNK_URL = 'files/chunk-file-upload';
    private static END_CHUNKED_FILE_CHUNK_URL = 'files/chunk-file-upload/finish';

    private onProgressUpdateListeners: ((progress: number) => void)[] = [];
    private onUploadFinishedListeners: ((result: RETURN_TYPE) => void)[] = [];

    private uploadingFile = false;
    private totalChunks = 0;
    private fileHandle: string | undefined = undefined;

    constructor(
        private ajaxService: AjaxService,
        private toastMessageService: ToastMessageService,
        private uploadEndpoint: string,
        private file: File,
        private additionalData: Record<string, any>,
        onProgressUpdate: (progress: number) => void,
        onUploadFinished: (result: RETURN_TYPE) => void,
    ) {
        this.onProgressUpdateListeners.push(onProgressUpdate);
        this.onUploadFinishedListeners.push(onUploadFinished);
    }

    public async upload(): Promise<RETURN_TYPE> {
        if (this.uploadingFile) {
            return Promise.reject('File is already uploading');
        }

        if (!exists(this.file) || this.file.size === 0) {
            return Promise.reject('File is empty or does not exist');
        }

        let handle: string;

        try {
            const requestData = {
                filename: this.file.name,
                size: this.file.size,
                ...this.additionalData
            };

            const response = await this.ajaxService
                .createRequest(this.uploadEndpoint)
                .post<StartChunkFileUploadResponse>(transferify(requestData))
                .catch((errorResponse) => {
                    if ('simpleName' in errorResponse && errorResponse.simpleName === 'ChunkUploadFileTooLargeException') {
                        this.toastMessageService.showError('Soubor je příliš velký');
                    }

                    return Promise.reject(errorResponse);
                });

            handle = response.chunkedFileHandle;
        } catch {
            return Promise.reject('Failed to start chunked file upload');
        }

        this.fileHandle = handle;
        this.uploadingFile = true;

        let chunkIndex = 0;
        this.totalChunks = Math.ceil(this.file.size / ChunkFileUploader.CHUNK_SIZE);

        for (let start = 0; start < this.file.size; start += ChunkFileUploader.CHUNK_SIZE) {
            if (!this.uploadingFile) {
                break;
            }

            const chunk = this.file.slice(start, start + ChunkFileUploader.CHUNK_SIZE);
            const success = await this.uploadFileChunk(this.fileHandle, start, chunk, chunkIndex);

            if (!success) {
                return Promise.reject(`Failed to upload file chunk ${chunkIndex + 1}/${this.totalChunks}`);
            }

            chunkIndex++;
        }

        if (this.uploadingFile) {
            return this.endFileUpload(this.fileHandle, false);
        }

        return Promise.reject('Unexpected error');
    }

    private async uploadFileChunk(fileId: string, start: number, chunk: Blob, chunkIndex: number): Promise<boolean> {
        const end = start + chunk.size - 1;

        try {
            await this.ajaxService
                .createRequest(`${ChunkFileUploader.UPLOAD_FILE_CHUNK_URL}/${fileId}`)
                .withHeaders({
                    'Content-Type': chunk.type || 'application/octet-stream',
                    'Range': `bytes=${start}-${end}`
                })
                .post(chunk);
        } catch {
            return false;
        }

        const progressPercentage = Math.round(((chunkIndex + 1) / this.totalChunks) * 100);
        this.onProgressUpdateListeners.forEach((e) => e(progressPercentage));

        return true;
    }

    private async endFileUpload(fileId: string, quitUpload: boolean): Promise<RETURN_TYPE> {
        this.uploadingFile = false;

        try {
            const endUploadResult = await this.ajaxService
                .createRequest(`${ChunkFileUploader.END_CHUNKED_FILE_CHUNK_URL}/${fileId}`)
                .post<RETURN_TYPE>({
                    quitUpload
                });

            if (quitUpload) {
                return Promise.reject('Upload canceled');
            }

            this.onUploadFinishedListeners.forEach((e) => e(endUploadResult));
            return endUploadResult;
        } catch {
            return Promise.reject('Failed to end file upload');
        }
    }

    public addOnProgressUpdateListener(listener: (progress: number) => void): void {
        this.onProgressUpdateListeners.push(listener);
    }

    public addOnUploadFinishedListener(listener: (result: RETURN_TYPE) => void): void {
        this.onUploadFinishedListeners.push(listener);
    }

    public removeOnProgressUpdateListener(listener: (progress: number) => void): void {
        this.onProgressUpdateListeners = this.onProgressUpdateListeners.filter((e) => e !== listener);
    }

    public removeOnUploadFinishedListener(listener: (result: RETURN_TYPE) => void): void {
        this.onUploadFinishedListeners = this.onUploadFinishedListeners.filter((e) => e !== listener);
    }
}